/**
 * Catches render errors that would otherwise blank the page, reports them
 * (tagged with the current session) so a bug a user hits can be looked up
 * instead of reproduced, and shows a recoverable fallback.
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { apiClient } from '@/api/client';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    apiClient.errors.report({
      message: error.message,
      stack: error.stack,
      component_stack: info.componentStack ?? undefined,
      url: window.location.href,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '3rem 1.5rem', textAlign: 'center' }}>
          <h2>Something went wrong.</h2>
          <p>The error has been logged. Try reloading the page.</p>
          <button onClick={() => window.location.reload()}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}
